interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: {
    user?: { id: number; first_name: string; last_name?: string; username?: string };
    /** Value of the `startapp` deep-link param the Mini App was opened with. */
    start_param?: string;
  };
  colorScheme?: 'light' | 'dark';
  themeParams?: Record<string, string>;
  ready: () => void;
  expand: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  onEvent?: (event: 'themeChanged' | 'fullscreenChanged' | 'fullscreenFailed' | string, cb: () => void) => void;
  offEvent?: (event: 'themeChanged' | 'fullscreenChanged' | 'fullscreenFailed' | string, cb: () => void) => void;
  BackButton?: { show: () => void; hide: () => void; onClick: (cb: () => void) => void };
  /** 'android' | 'ios' | 'tdesktop' | 'web' | ... */
  platform?: string;
  /** True if the client supports at least Bot API version `v`. */
  isVersionAtLeast?: (v: string) => boolean;
  // Mini App fullscreen (Bot API 8.0+). On Android the native HTML5/iframe
  // fullscreen is broken, so we drive these instead.
  isFullscreen?: boolean;
  requestFullscreen?: () => void;
  exitFullscreen?: () => void;
  /** Opens a t.me/… link inside Telegram and minimizes the Mini App. */
  openTelegramLink?: (url: string) => void;
  /** Opens an arbitrary external URL (in Telegram's in-app browser). */
  openLink?: (url: string) => void;
  close?: () => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getTelegramWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function getInitData(): string {
  return getTelegramWebApp()?.initData ?? '';
}

export function isInsideTelegram(): boolean {
  return Boolean(getInitData());
}

/** Telegram client platform ('android' | 'ios' | 'tdesktop' | 'web' | …), or
 *  '' when not running inside Telegram. */
export function getTelegramPlatform(): string {
  return getTelegramWebApp()?.platform ?? '';
}

/**
 * The deep-link start param the app was launched with.
 *
 * Primary source is Telegram's `initDataUnsafe.start_param`. As a fallback we
 * also read it off the launch URL (`?startapp=` / `?tgWebAppStartParam=`) — that
 * keeps deep links testable in a plain browser during dev, where there's no
 * Telegram wrapper to populate initData.
 */
export function getStartParam(): string {
  const fromTelegram = getTelegramWebApp()?.initDataUnsafe?.start_param;
  if (fromTelegram) return fromTelegram;
  const qs = new URLSearchParams(window.location.search);
  return qs.get('startapp') ?? qs.get('tgWebAppStartParam') ?? '';
}

/**
 * Click handler for an anchor pointing at a t.me/… bot link. Inside Telegram a
 * plain `<a target="_blank">` opens the in-app browser and leaves the Mini App
 * on screen, so the user thinks nothing happened. `openTelegramLink` instead
 * hands the link to the Telegram client, which opens the bot chat (processing
 * the ?start= param) and minimizes the Mini App. Outside Telegram (browser/dev)
 * we let the anchor open normally.
 */
export function openBotLink(e: MouseEvent, url: string): void {
  const tg = getTelegramWebApp();
  if (!tg) return; // outside Telegram → let the <a> open the link normally
  e.preventDefault();
  if (tg.openTelegramLink) tg.openTelegramLink(url);
  else window.open(url, '_blank', 'noopener');
  // openTelegramLink only *minimizes* the Mini App on most clients, so the user
  // thinks nothing happened. Explicitly close it so they land in the bot chat.
  tg.close?.();
}

/**
 * Opens an arbitrary external URL (e.g. a Zoom/YouTube stream link). Inside
 * Telegram uses the in-app browser (Mini App stays open); otherwise a new tab.
 */
export function openExternalLink(url: string): void {
  const tg = getTelegramWebApp();
  if (tg?.openLink) tg.openLink(url);
  else window.open(url, '_blank', 'noopener');
}
